// ambient.ts — PWA V2 slice 4 (STEP-PWA-UXV2-INTEGRATE-LEGACY).
//
// Ports the legacy Shell/UnifiedHeader ambient behaviour into the v4 shell:
// polls /api/pwa/dashboard and derives calm/attention/alert from the
// attentionFeed via the SAME deriveState the legacy header uses, plus the
// kill-switch state machine (armed when alert; operator can PAUSE/RESUME).

import { useCallback, useEffect, useMemo, useState } from "react";
import { api, isAuthError } from "../api/client.js";
import { deriveState } from "../components/Shell.js";
import type { AmbientState } from "../components/UnifiedHeader.js";
import type { DashboardResponse } from "../api/types.js";

export interface AmbientValue {
  state: AmbientState;
  pulse: string;
  killable: boolean;
  killed: boolean;
  toggleKilled: () => void;
}

export function useAmbient(): AmbientValue {
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  // DEC-166: a connectivity failure must never render as "all clear". Track
  // fetch failures explicitly so the header resolves out of "connecting..."
  // into an honest error state instead of sticking calm forever.
  const [apiError, setApiError] = useState<unknown>(null);
  const apiDown = apiError !== null;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const d = await api.dashboard();
        if (!cancelled) {
          setDashboard(d);
          setApiError(null);
        }
      } catch (err) {
        // still retry on the next interval, but surface the failure now
        if (!cancelled) setApiError(err);
      }
    };
    void load();
    const interval = setInterval(() => void load(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const derived = useMemo(() => deriveState(dashboard), [dashboard]);
  // An auth expiry is not a system alert — the fetch layer is already
  // navigating the top-level window to sign-in (api/client.ts
  // triggerSignInRedirect). Never brand it "alert" or "unreachable"; that's
  // exactly how the operator got trapped reading a dead-end banner twice
  // (Jul-8) instead of seeing an honest "redirecting" state.
  const authExpired = apiDown && isAuthError(apiError);
  const state: AmbientState = authExpired
    ? derived.state
    : apiDown
      ? "alert"
      : derived.state;
  const pulse = authExpired
    ? "↻ Session expired — redirecting to sign-in…"
    : apiDown
      ? dashboard
        ? "⚠ API unreachable — showing cached data"
        : "⚠ API unreachable — check connection"
      : derived.pulse;

  // Kill-switch — same semantics as the legacy Shell: auto-engages when the
  // SYSTEM enters alert; the operator can flip it (RESUME/PAUSE) while alert.
  // Keyed on the derived (data-driven) state, not the connectivity override:
  // an unreachable API is a visibility failure, not a system alert — the
  // kill-switch is meaningless when no command can reach the API anyway.
  const [killed, setKilled] = useState(false);
  useEffect(() => {
    setKilled(derived.state === "alert");
  }, [derived.state]);

  const toggleKilled = useCallback(() => setKilled((k) => !k), []);

  return {
    state,
    pulse,
    killable: !apiDown && derived.state === "alert",
    killed,
    toggleKilled,
  };
}
